import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Wrench, Box } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import KineticMarquee from "../components/KineticMarquee";
import ProductCard from "../components/ProductCard";
import { api } from "../lib/api";

const HERO_IMG = "/hero-racing.png";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/coming-soon").then((r) => setComingSoon(r.data)).catch(() => {});
  }, []);

  // bento layout spans
  const spans = [
    "md:col-span-7",
    "md:col-span-5",
    "md:col-span-5",
    "md:col-span-7",
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />

      {/* HERO */}
      <section className="relative" data-testid="home-hero">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt="F1 racing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-40 md:pt-44 md:pb-56">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2800] mb-6 flex items-center gap-3"
          >
            <span className="w-8 h-px bg-[#FF2800]" /> // Drop 01 — Now shipping in India
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="af-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-[1.02] max-w-5xl"
          >
            F1 frames &
            <br />
            <span className="af-outline-text">3D printed models.</span>
            <br />
            Your vision, built.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-zinc-300 text-base md:text-lg max-w-2xl mt-8 leading-relaxed"
          >
            Apex Designs crafts collector-grade brick-built F1 car frames, sim-racing
            gear and 3D-printed motorsport models. Engineered for shelves that
            mean business.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/shop"
              className="inline-flex items-center justify-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 px-8 hover:bg-[#CC2000] transition-colors"
              data-testid="hero-shop-button"
            >
              Shop <ArrowRight size={16} />
            </Link>
            <Link
              to="/custom-request"
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold uppercase tracking-wider py-4 px-8 hover:bg-white hover:text-black transition-colors"
              data-testid="hero-custom-button"
            >
              Request custom build
            </Link>
          </motion.div>

          <div className="mt-20 grid grid-cols-3 gap-6 max-w-3xl">
            {[
              { k: "1,200+", v: "Avg. piece count" },
              { k: "1:14", v: "Display scale" },
              { k: "48 hr", v: "Mumbai dispatch" },
            ].map((s) => (
              <div
                key={s.v}
                className="border-l border-white/15 pl-4"
              >
                <p className="af-display text-2xl md:text-3xl font-black tracking-tight">
                  {s.k}
                </p>
                <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">
                  {s.v}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <KineticMarquee />

      {/* FEATURED FRAMES */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="featured-frames-section"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
              // 01 · The Grid
            </p>
            <h2 className="af-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
              Four frames.
              <br />
              One obsession.
            </h2>
          </div>
          <Link
            to="/shop"
            className="af-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300 hover:text-white inline-flex items-center gap-2"
            data-testid="featured-view-all"
          >
            View all <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {products
            .filter((p) => (p.category || "f1") !== "fortnite")
            .map((p, i) => (
              <ProductCard
                key={p.slug}
                product={p}
                span={spans[i % spans.length]}
              />
            ))}
        </div>
      </section>

      {/* COMING SOON */}
      <section
        className="bg-[#080808] border-y border-white/10"
        data-testid="coming-soon-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
                // 02 · Drop incoming
              </p>
              <h2 className="af-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
                Coming soon
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {comingSoon.map((c, i) => (
              <Link
                to={`/product/${c.slug}`}
                key={c.slug}
                className={`af-card relative overflow-hidden group block ${
                  i === 0 ? "md:col-span-7" : "md:col-span-5"
                }`}
                data-testid={`coming-soon-card-${c.slug}`}
              >
                <div className="aspect-[16/10] relative overflow-hidden bg-black">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40" />
                  <span className="absolute top-4 left-4 af-mono text-[10px] uppercase tracking-[0.25em] bg-[#00E5FF] text-black px-2 py-1">
                    Soon
                  </span>
                  <span className="absolute top-4 right-4 af-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                    {c.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="af-display text-2xl md:text-3xl font-bold uppercase tracking-tight">
                    {c.name}
                  </h3>
                  <p className="text-zinc-400 mt-2 text-sm">{c.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM CTA + FOUNDER MINI */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7 af-card p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#FF2800]/15 blur-3xl rounded-full" />
          <p className="af-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2800] mb-4 relative">
            // 03 · Custom workshop
          </p>
          <h3 className="af-display text-3xl md:text-5xl font-bold uppercase tracking-tight relative">
            Can't find your model?
            <br />
            We'll build it.
          </h3>
          <p className="text-zinc-300 mt-6 max-w-xl relative">
            Whether it's a vintage grand-prix legend or a hypercar from your
            dream garage — submit a custom request and our workshop will
            engineer it brick-by-brick.
          </p>
          <Link
            to="/custom-request"
            className="mt-8 inline-flex items-center gap-2 bg-[#FF2800] text-white font-semibold uppercase tracking-wider py-4 px-8 hover:bg-[#CC2000] transition-colors relative"
            data-testid="home-custom-cta"
          >
            Start a request <ArrowRight size={16} />
          </Link>
        </div>

        <div className="md:col-span-5 flex flex-col gap-6">
          <div className="af-card p-8">
            <Sparkles size={20} strokeWidth={1.5} className="text-[#FF2800]" />
            <h4 className="af-display text-xl font-bold uppercase tracking-tight mt-4">
              Founder-built
            </h4>
            <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
              Apex Designs was founded by Riaan Vatsa, a 7th grader who builds
              in his free time — between homework and sim-racing laps.
            </p>
            <Link
              to="/about"
              className="af-mono text-[11px] uppercase tracking-[0.25em] text-white mt-4 inline-flex items-center gap-2"
              data-testid="home-about-link"
            >
              Read the story <ArrowRight size={12} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="af-card p-6">
              <Wrench size={18} strokeWidth={1.5} className="text-[#00E5FF]" />
              <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-4">
                Hand-finished
              </p>
              <p className="af-display text-lg font-semibold mt-1">
                Studio polish
              </p>
            </div>
            <div className="af-card p-6">
              <Box size={18} strokeWidth={1.5} className="text-[#00E5FF]" />
              <p className="af-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-4">
                Display-ready
              </p>
              <p className="af-display text-lg font-semibold mt-1">
                Custom stand
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
